import { useCallback } from 'react';
import { useDefaultSuccessHandlers } from '@antimatterhq/ui/contexts/DefaultSuccessHandlerContext';

/**
 * @description Provides a default success handling function for requests,
 * primarily designed for use within React Query wrappers (`useQuery`, `useMutation`).
 * It delegates handling to handlers provided by `DefaultSuccessHandlerContext`.
 *
 * @returns {function(data: T): Promise<void>} An asynchronous function (`handleSuccess`)
 * that takes the successful response data. It calls the default success handler
 * provided by the context.
 */
export function useDefaultRequestSuccessHandling() {
  const { defaultSuccessHandler } = useDefaultSuccessHandlers();

  /**
   * @description The main success handler function returned by the hook.
   * It calls the default success handler when a request succeeds.
   * @returns {Promise<void>} A Promise that resolves when the success handler completes.
   */
  const handleSuccess = useCallback(async (): Promise<void> => {
    await defaultSuccessHandler();
  }, [defaultSuccessHandler]);

  return handleSuccess;
}
